import { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';
import useEventListener from '../../hooks/useEventListener';

// ----------------------------------------------------------------------

type Props = {
  keyName: string;
  inputs: string[];
  placeHolder?: string;
  onPaste?(value: Props['inputs']): void;
};

export default function RHFCodes({ keyName = '', inputs = [], placeHolder = '-', onPaste }: Props) {
  const codesRef = useRef<HTMLDivElement>(null);

  const { control, setValue } = useFormContext();

  const handlePaste = (event: ClipboardEvent) => {
    const data = event.clipboardData?.getData('text') || '';

    const chars = data.split('');

    inputs.forEach((input, index) => setValue(input, chars[index]));

    if (onPaste) {
      onPaste(chars);
    }

    event.preventDefault();
  };

  const handleChangeWithNextField = (
    event: React.ChangeEvent<HTMLInputElement>,
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    const { maxLength, value, name } = event.target;

    const fieldIndex = name.replace(keyName, '');

    const fieldIntIndex = Number(fieldIndex);

    const nextfield: HTMLElement | null = document.querySelector(
      `input[name=${keyName}${fieldIntIndex + 1}]`
    );

    if (value.length > maxLength) {
      event.target.value = value[0];
    }

    if (value.length >= maxLength && fieldIntIndex < 6 && nextfield !== null) {
      (nextfield as HTMLElement).focus();
    }

    handleChange(event);
  };

  useEventListener('paste', handlePaste, codesRef);

  return (
    <div className="flex w-full flex-row justify-between gap-3" ref={codesRef}>
      {inputs.map((name, index) => (
        <Controller
          key={name}
          name={`${keyName}${index + 1}`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              autoFocus={index === 0}
              placeholder={placeHolder}
              onKeyUp={(event) => {
                if (event.key === 'Backspace') {
                  const lastfield: HTMLElement | null = document.querySelector(
                    `input[name=${keyName}${index}]`
                  );
                  if (lastfield) {
                    lastfield.focus();
                  }
                }
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleChangeWithNextField(event, field.onChange);
              }}
              onFocus={(event) => event.currentTarget.select()}
              maxLength={1}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={cn(
                'h-10 w-10 text-center text-lg sm:h-12 sm:w-12',
                error && 'border-destructive'
              )}
            />
          )}
        />
      ))}
    </div>
  );
}
